import { Popover } from '@mui/material';
import { ReactNode, useCallback, useEffect, useRef } from 'react';

import { COLORS } from '@/styles/tokens/colors';
import { zIndexes } from '@/styles/zIndexes';

import { useSlideEditorToolbarContext } from './SlideEditor.toolbar';

/**
 * Hook that registers a dropdown with the toolbar's capture-phase click router.
 * Returns a containerRef that should be placed on the wrapper element around
 * the dropdown trigger button.
 */
export function useToolbarDropdown(key: string) {
  const { registerDropdown, unregisterDropdown } =
    useSlideEditorToolbarContext();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    registerDropdown(key, containerRef);
    return () => unregisterDropdown(key);
  }, [key, registerDropdown, unregisterDropdown]);

  return { containerRef };
}

// 4px visual gap between the toolbar's bottom border and the popover's top edge.
const POPOVER_GAP = '4px';
// When anchored to a trigger button inside the toolbar, we need to clear
// the toolbar's padding (4px) + border (1px) in addition to the 4px gap.
const POPOVER_MARGIN_TOP_FROM_BUTTON = '9px';

interface SlideEditorToolbarPopoverProps {
  /** Key used to identify this dropdown in the toolbar context */
  dropdownKey: string;
  /**
   * When true, the popover's left edge aligns with the toolbar's left edge
   * (first-item rule). When false, it aligns with the trigger button's left edge.
   */
  anchorToToolbar?: boolean;
  /** The trigger button. Receives `isOpen` so it can show active styling. */
  renderTrigger: (props: { isOpen: boolean }) => ReactNode;
  /** The popover content (menu items, form, etc.) */
  children: ReactNode;
  /** Optional width for the popover paper. */
  width?: number;
  /** Optional height for the popover paper. */
  height?: number;
  /** Optional additional paper styles */
  paperSx?: Record<string, unknown>;
}

/**
 * Standardized popover container for slide editor toolbar dropdowns.
 *
 * Handles:
 * - Registration with the toolbar context for capture-phase click routing
 * - Consistent 4px gap between toolbar bottom and popover top
 * - Left-edge alignment to toolbar (first item) or trigger button (non-first)
 * - Open/close coordination via toolbar activeDropdown state
 */
export function SlideEditorToolbarPopover({
  dropdownKey,
  anchorToToolbar = false,
  renderTrigger,
  children,
  width,
  height,
  paperSx,
}: SlideEditorToolbarPopoverProps) {
  const { activeDropdown, setActiveDropdown, toolbarRef } =
    useSlideEditorToolbarContext();
  const { containerRef } = useToolbarDropdown(dropdownKey);
  const triggerRef = useRef<HTMLDivElement>(null);

  const isOpen = activeDropdown === dropdownKey;

  const handleToggle = useCallback(() => {
    setActiveDropdown((prev) => (prev === dropdownKey ? null : dropdownKey));
  }, [dropdownKey, setActiveDropdown]);

  const handleClose = useCallback(() => {
    setActiveDropdown((prev) => (prev === dropdownKey ? null : prev));
  }, [dropdownKey, setActiveDropdown]);

  const anchorEl = anchorToToolbar ? toolbarRef.current : triggerRef.current;

  // When anchored to the toolbar, the anchor's bottom edge IS the toolbar
  // border — only a 4px visual gap is needed. When anchored to the trigger
  // button, we need 9px (5px toolbar padding+border + 4px gap).
  const marginTop = anchorToToolbar
    ? POPOVER_GAP
    : POPOVER_MARGIN_TOP_FROM_BUTTON;

  return (
    <div ref={containerRef}>
      {/* onClick is on the trigger only — not the outer container — so that
          clicks inside the Popover (e.g. Cancel, backdrop) don't bubble up
          through the React portal tree and re-toggle the dropdown. */}
      <div ref={triggerRef} onClick={handleToggle}>
        {renderTrigger({ isOpen })}
      </div>
      <Popover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        // Disable viewport margin so the popover aligns exactly with the
        // anchor's left edge without being pushed inward.
        marginThreshold={0}
        // Elevate above the toolbar Popper (zIndexes.POPOVER) so the panel
        // renders on top of the toolbar's box-shadow.
        sx={{ zIndex: zIndexes.TOOLTIP }}
        slotProps={{
          paper: {
            sx: {
              mt: marginTop,
              border: `1px solid ${COLORS.NAVY[100]}`,
              borderRadius: '8px',
              ...(width ? { width } : {}),
              ...(height ? { height } : {}),
              ...paperSx,
            },
          },
        }}
      >
        {children}
      </Popover>
    </div>
  );
}
